<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { Head } from '@inertiajs/vue3'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/solid'
import InternalLayout from '@/layouts/InternalLayout.vue'

interface LocationSummary {
    name: string;
    state: string | null;
}

const props = defineProps<{
    locations?: LocationSummary[];
}>();

let jsonLdScript: HTMLScriptElement | null = null

onMounted(() => {
    jsonLdScript = document.createElement('script')
    jsonLdScript.type = 'application/ld+json'

    const areaServed = props.locations?.length
        ? props.locations.map((loc) => ({
              '@type': 'City',
              name: loc.name,
              containedInPlace: {
                  '@type': 'State',
                  name: loc.state ?? 'TX',
              },
          }))
        : [{ '@type': 'State', name: 'Texas' }]

    jsonLdScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Lawn Care',
        provider: {
            '@type': 'LocalBusiness',
            '@id': 'https://mowmanagers.com/#business',
            name: 'Mow Managers',
        },
        areaServed,
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Lawn Care Services',
            itemListElement: [
                {
                    '@type': 'Offer',
                    itemOffered: {
                        '@type': 'Service',
                        name: 'Lawn Mowing',
                    },
                },
                {
                    '@type': 'Offer',
                    itemOffered: {
                        '@type': 'Service',
                        name: 'Line Trimming',
                    },
                },
                {
                    '@type': 'Offer',
                    itemOffered: {
                        '@type': 'Service',
                        name: 'Edging',
                    },
                },
                {
                    '@type': 'Offer',
                    itemOffered: {
                        '@type': 'Service',
                        name: 'Debris Cleanup',
                    },
                },
            ],
        },
    })
    document.head.appendChild(jsonLdScript)
})

onUnmounted(() => {
    jsonLdScript?.remove()
})

const services = [
  {
    title: 'Residential Mowing',
    description: 'Proper lawn care takes experience and professionalism to do the job right. That is why we will provide you with a mowing schedule and mow date that works best for you. Mow Managers will connect you with experienced professionals that are trustworthy and reliable, with the expertise necessary to cut your grass at the appropriate height to keep your lawn looking and feeling healthy. Mow Managers makes lawn care easy, fast and reliable for you.',
    image: '/img/below-header-left.png',
  },
  {
    title: 'Line Trim',
    description: 'At Mow Managers, we take pride in ensuring your lawn is done right every time. We line trim around the outside of flowerbed borders, around driveways, walkways, stepping stones, the edge of your house, fence and more. We can also define your flowerbed with trench style borders upon request. No matter the job, our trusted, reliable lawn care experts get the job done right.',
    image: '/img/local-lawn-care.webp',
  },
  {
    title: 'Edge',
    description: 'Mow Managers makes lawn care and maintenance easy and hassle-free. Part of making your lawn look its best is having the right tools and know-how to get the job done right. Our experienced lawn care experts will edge your lawn, leaving it looking and feeling good.',
    image: '/img/below-header-middle.webp',
  },
  {
    title: 'Blow Debris',
    description: 'At Mow Managers, our highly experienced team of lawn care professionals will mow, line trim, edge, and then blow debris and grass from your concrete areas. All that\'s left for you to do is sit back, relax, and enjoy your freshly mowed lawn.',
    image: '/img/below-header-right.webp',
  },
]

const included = [
  'We mow with mulching mowers, line trim, edge, and blow concrete areas clean.',
  'The mow day is guaranteed (excluding weather delays or equipment failure), but we do not guarantee the time of day the crew will arrive.',
]

const notIncluded = [
  'We do not clean up pet waste, move furniture, or other items on the lawn, pull weeds, apply fertilizer or spray any chemicals.',
  'Ask us the price details of our bush & bed crew or bagging grass.',
]
</script>

<template>
  <InternalLayout title="Services" hero-image="/img/professional-backyard-lawn-care.jpg">
    <Head title="Services">
      <meta head-key="description" name="description" content="Mow Managers lawn care services include residential mowing, line trimming, edging, and debris cleanup. Trusted local pros for a freshly cut lawn." />
      <link rel="canonical" href="https://mowmanagers.com/services" />
      <meta property="og:title" content="Services - Mow Managers" />
      <meta property="og:description" content="Mow Managers lawn care services include residential mowing, line trimming, edging, and debris cleanup." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/services" />
    </Head>

    <!-- ==================== SERVICE SECTIONS ==================== -->
    <section aria-labelledby="services-heading" class="py-20 lg:py-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 id="services-heading" class="sr-only">Our Lawn Care Services</h2>
        <div class="space-y-20 lg:space-y-28">
          <article
            v-for="(service, index) in services"
            :key="service.title"
            class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <div :class="index % 2 === 1 ? 'lg:order-2' : ''">
              <div class="rounded-[20px] overflow-hidden">
                <img
                  :src="service.image"
                  :alt="`Mow Managers ${service.title.toLowerCase()} service`"
                  class="w-full h-[300px] lg:h-[380px] object-cover"
                  width="640"
                  height="380"
                  loading="lazy"
                />
              </div>
            </div>

            <div :class="index % 2 === 1 ? 'lg:order-1' : ''">
              <p class="text-mm-green text-[14px] font-extrabold uppercase tracking-[2.8px]">
                Services
              </p>
              <h3 class="text-mm-green text-3xl sm:text-4xl lg:text-[48px] font-bold lg:leading-[56px] mt-2">
                {{ service.title }}
              </h3>
              <div class="w-12 h-[3px] bg-mm-green mt-4" aria-hidden="true" />
              <p class="text-[#6d706d] text-[16px] font-medium mt-6 leading-[26px]">
                {{ service.description }}
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== INCLUDED / NOT INCLUDED ==================== -->
    <section aria-labelledby="included-heading" class="bg-mm-light py-16 lg:py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 id="included-heading" class="sr-only">What's Included and Not Included</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div class="flex items-center gap-3 mb-4">
              <CheckCircleIcon class="text-mm-green size-8" aria-hidden="true" />
              <h3 class="text-mm-dark text-[18px] font-bold uppercase tracking-[1.4px]">Services Included:</h3>
            </div>
            <ul class="space-y-3 list-none">
              <li
                v-for="item in included"
                :key="item"
                class="text-[#6d706d] text-[15px] font-medium leading-[24px]"
              >
                {{ item }}
              </li>
            </ul>
          </div>

          <div>
            <div class="flex items-center gap-3 mb-4">
              <XCircleIcon class="text-red-500 size-8" aria-hidden="true" />
              <h3 class="text-mm-dark text-[18px] font-bold uppercase tracking-[1.4px]">Services Not Included:</h3>
            </div>
            <ul class="space-y-3 list-none">
              <li
                v-for="item in notIncluded"
                :key="item"
                class="text-[#6d706d] text-[15px] font-medium leading-[24px]"
              >
                {{ item }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </InternalLayout>
</template>
